import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { markNotificationAsRead } from '../../redux/slices/user';
import { formatDistanceToNow } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import { getAvatarImagePath } from '../../common/getImagePath';
import {
  NOTIFICATION_ACTIONS,
  NOTIFICATION_TYPES_WITH_ACTIONS
} from '../../common/notificationActions';
import useLocales from '../../hooks/useLocales';
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListItemAvatar,
  CircularProgress
} from '@mui/material';
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { MIconButton } from '../../components/@material-extend';
import type { RootState } from '../../redux/store';
import { useDispatch, useSelector } from '../../redux/store';
import type { Notification } from '../../@types/user';

const POPOVER_MAX_UNREAD = 5;

export function NotificationPopoverItem({
  notification,
  onClose
}: {
  notification: Notification;
  onClose?: () => void;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const actions = NOTIFICATION_ACTIONS[notification.type] ?? [];
  const hasActions = actions.length > 0;
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const handleClick = () => {
    if (notification.isUnRead) {
      dispatch(markNotificationAsRead(notification.guid));
    }
    if (notification.linkUrl) {
      onClose?.();
      navigate(notification.linkUrl);
    }
  };

  const handleAction = async (e: React.MouseEvent, action: (typeof actions)[number]) => {
    e.stopPropagation();
    setPendingAction(action.labelKey);
    try {
      await action.handle(notification, dispatch);
      enqueueSnackbar(translate(action.successKey), { variant: 'success' });
    } catch {
      enqueueSnackbar(translate(action.errorKey), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <ListItem
      disableGutters
      onClick={handleClick}
      sx={{
        py: 1.5,
        px: 2,
        gap: 1,
        alignItems: 'flex-start',
        cursor: notification.isUnRead || notification.linkUrl ? 'pointer' : 'default',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
        ...(hasActions && { display: 'block' })
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <ListItemAvatar sx={{ mt: 0, minWidth: 'unset' }}>
          <Avatar
            src={getAvatarImagePath(notification.imageUrl)}
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'background.neutral',
              ...(notification.isUnRead && {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px'
              })
            }}
          />
        </ListItemAvatar>

        <ListItemText
          sx={{ m: 0 }}
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.isUnRead ? 'fontWeightBold' : 'fontWeightRegular',
                lineHeight: 1.4
              }}
            >
              {notification.title}
            </Typography>
          }
          secondary={
            <Typography
              variant="caption"
              sx={{ mt: 0.5, display: 'flex', alignItems: 'center', color: 'text.disabled' }}
            >
              <AccessTimeIcon sx={{ mr: 0.5, fontSize: 'inherit' }} />
              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
            </Typography>
          }
        />

        {notification.isUnRead && (
          <Box
            sx={{
              width: 8,
              height: 8,
              mt: 1,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              flexShrink: 0
            }}
          />
        )}
      </Box>

      {hasActions && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, pl: 6.5 }}>
          {actions.map((action) => {
            const isLoading = pendingAction === action.labelKey;
            return (
              <Button
                key={action.labelKey}
                size="small"
                variant={action.variant}
                color={action.color}
                disabled={pendingAction !== null}
                onClick={(e) => handleAction(e, action)}
                startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : undefined}
              >
                {translate(action.labelKey)}
              </Button>
            );
          })}
        </Box>
      )}
    </ListItem>
  );
}

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const { translate } = useLocales();
  const navigate = useNavigate();

  const { notifications, notificationsUnreadCount } = useSelector((state: RootState) => state.user);

  const totalUnRead = notificationsUnreadCount;
  const unreadItems =
    notifications
      ?.filter((item) => item.isUnRead || NOTIFICATION_TYPES_WITH_ACTIONS.has(item.type))
      .slice(0, POPOVER_MAX_UNREAD) ?? [];

  const handleViewAll = () => {
    setOpen(false);
    navigate('/dashboard/notifications');
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={() => setOpen(true)}
        color={open ? 'primary' : 'default'}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <NotificationsOutlined />
        </Badge>
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        sx={{ width: 380 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">
              {translate('components.inbox_popover.title')}
            </Typography>
            {totalUnRead > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {translate('components.inbox_popover.unread', { count: totalUnRead })}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {unreadItems.length === 0 ? (
          <Box sx={{ py: 4, px: 2.5, textAlign: 'center' }}>
            <NotificationsOutlined sx={{ color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {translate('components.inbox_popover.no_notifications')}
            </Typography>
          </Box>
        ) : (
          <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
            <List disablePadding>
              {unreadItems.map((notification) => (
                <NotificationPopoverItem
                  key={notification.guid}
                  notification={notification}
                  onClose={() => setOpen(false)}
                />
              ))}
            </List>
          </Scrollbar>
        )}

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" onClick={handleViewAll}>
            {translate('components.inbox_popover.view_all')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
